"use client";
import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
    fetchPurchases,
    fetchPurchaseDetails,
    addPurchasePayment,
    reset,
    clearCurrentPurchase
} from "@/state/purchases/purchase.slice";
import { fetchBusiness } from "@/state/business/business.slice";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
} from "@/components/ui/sheet";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    DotsThreeVertical,
    MagnifyingGlass,
    Eye,
} from "@phosphor-icons/react";
import { format } from "date-fns";
import { Loader2, RefreshCcw, DollarSign, Package, Undo2, Pencil, Trash2 } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { usePermission } from "@/hooks/usePermission";

export default function PurchasesPage() {
    const dispatch = useDispatch();
    // Client-side navigation. `window.location.href` here would do a full
    // browser document load, re-booting the whole SPA (re-auth + refetch).
    const navigate = useNavigate();
    const { toast } = useToast();
    const { purchases, loading, pagination, currentPurchase, success, error, message } = useSelector((state) => state.purchases);
    const { business } = useSelector((state) => state.business || {});
    const [searchTerm, setSearchTerm] = useState("");
    const [page, setPage] = useState(1);
    const [limit] = useState(20);
    const [paymentFilter, setPaymentFilter] = useState("all");
    // Default to showing all purchases - user can filter by date
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");

    // Dialog/Sheet states
    const [detailsSheetOpen, setDetailsSheetOpen] = useState(false);
    const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
    const [selectedPurchase, setSelectedPurchase] = useState(null);

    // Permissions
    const canAcceptPayments = usePermission('AcceptPayments');
    const canManagePayments = usePermission('ManagePayments');

    // Form states
    const [paymentAmount, setPaymentAmount] = useState("");
    const [paymentMethod, setPaymentMethod] = useState("Cash");
    const [paymentNotes, setPaymentNotes] = useState("");
    const [editingPayment, setEditingPayment] = useState(null);
    const [deletePaymentId, setDeletePaymentId] = useState(null);
    const [isPaymentSubmitting, setIsPaymentSubmitting] = useState(false);

    useEffect(() => {
        dispatch(fetchBusiness());
        dispatch(fetchPurchases({
            page,
            limit,
            search: searchTerm,
            payment_status: paymentFilter === "all" ? undefined : paymentFilter,
            startDate,
            endDate
        }));
    }, [dispatch, page, limit, searchTerm, paymentFilter, startDate, endDate]);

    useEffect(() => {
        if (error) {
            toast({ variant: "destructive", title: "Error", description: error });
            dispatch(reset());
        }
        if (success && message) {
            toast({ title: "Success", description: message });
            dispatch(reset());
            // Refresh list
            dispatch(fetchPurchases({
                page,
                limit,
                search: searchTerm,
                payment_status: paymentFilter === "all" ? undefined : paymentFilter,
                startDate,
                endDate
            }));
        }
    }, [error, success, message, toast, dispatch, page, limit, searchTerm, paymentFilter, startDate, endDate]);

    const handleRefresh = () => {
        dispatch(fetchPurchases({
            page,
            limit,
            search: searchTerm,
            payment_status: paymentFilter === "all" ? undefined : paymentFilter,
            startDate,
            endDate
        }));
    };

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
        setPage(1);
    };

    const statusColor = (status) => {
        switch (status) {
            case "paid": return "bg-green-100 text-green-800";
            case "partial": return "bg-yellow-100 text-yellow-800";
            case "unpaid": return "bg-red-100 text-red-800";
            default: return "bg-gray-100 dark:bg-slate-800 text-gray-800";
        }
    };

    const handleViewDetails = async (purchase) => {
        setSelectedPurchase(purchase);
        try {
            await dispatch(fetchPurchaseDetails(purchase.id)).unwrap();
            setDetailsSheetOpen(true);
        } catch (err) {
            toast({ variant: "destructive", title: "Error", description: "Failed to load purchase details" });
        }
    };

    const handleOpenPayment = (purchase) => {
        setSelectedPurchase(purchase);
        setPaymentAmount("");
        setPaymentMethod("Cash");
        setPaymentNotes("");
        setPaymentDialogOpen(true);
    };

    const handleSubmitPayment = async () => {
        if (!selectedPurchase || !paymentAmount) return;
        try {
            await dispatch(addPurchasePayment({
                id: selectedPurchase.id,
                paymentData: {
                    amount: parseFloat(paymentAmount),
                    payment_method: paymentMethod,
                    notes: paymentNotes,
                },
            })).unwrap();
            setPaymentDialogOpen(false);
        } catch (err) {
            // Error handled by useEffect
        }
    };

    const handleEditPayment = (payment) => {
        setEditingPayment(payment);
        setPaymentAmount(payment.amount.toString());
        setPaymentMethod(payment.payment_method);
        setPaymentNotes(payment.notes || "");
        setPaymentDialogOpen(true);
    };

    const handleUpdatePayment = async () => {
        if (!editingPayment || !paymentAmount || !currentPurchase) return;
        setIsPaymentSubmitting(true);
        try {
            const axios = (await import('axios')).default;
            await axios.put(`${import.meta.env.VITE_API_URL}purchases/${currentPurchase.id}/payments/${editingPayment.id}`, {
                amount: parseFloat(paymentAmount),
                payment_method: paymentMethod,
                notes: paymentNotes,
            }, { withCredentials: true });

            toast({ title: "Success", description: "Payment updated successfully" });
            setPaymentDialogOpen(false);
            setEditingPayment(null);
            setPaymentAmount("");
            // Refresh purchase details
            if (currentPurchase) {
                dispatch(fetchPurchaseDetails(currentPurchase.id));
            }
            dispatch(fetchPurchases({ page, limit, search: searchTerm, payment_status: paymentFilter === "all" ? undefined : paymentFilter, startDate, endDate }));
        } catch (err) {
            toast({ variant: "destructive", title: "Error", description: err.response?.data?.message || "Failed to update payment" });
        } finally {
            setIsPaymentSubmitting(false);
        }
    };

    const handleDeletePayment = async () => {
        if (!deletePaymentId || !currentPurchase) return;
        setIsPaymentSubmitting(true);
        try {
            const axios = (await import('axios')).default;
            await axios.delete(`${import.meta.env.VITE_API_URL}purchases/${currentPurchase.id}/payments/${deletePaymentId}`, { withCredentials: true });
            toast({ title: "Success", description: "Payment deleted successfully" });
            setDeletePaymentId(null);
            // Refresh purchase details
            if (currentPurchase) {
                dispatch(fetchPurchaseDetails(currentPurchase.id));
            }
            dispatch(fetchPurchases({ page, limit, search: searchTerm, payment_status: paymentFilter === "all" ? undefined : paymentFilter, startDate, endDate }));
        } catch (err) {
            toast({ variant: "destructive", title: "Error", description: err.response?.data?.message || "Failed to delete payment" });
        } finally {
            setIsPaymentSubmitting(false);
        }
    };

    const businessDetails = business || null;
    const currency = businessDetails?.currency || "GHS";

    return (
        <div className="mx-auto p-4 space-y-6">
            <div className="flex flex-wrap justify-between items-center gap-4">
                <div className="flex flex-wrap gap-4 items-center">
                    <h1 className="text-3xl font-bold">Supplier Purchases</h1>
                    <div className="flex items-center space-x-2 bg-white dark:bg-slate-900 px-2 border w-fit">
                        <MagnifyingGlass className="w-5 h-5 text-gray-500" />
                        <Input
                            placeholder="Search by invoice..."
                            value={searchTerm}
                            onChange={handleSearch}
                            className="border-none shadow-none focus-visible:ring-0 w-[150px]"
                        />
                    </div>
                    <Select value={paymentFilter} onValueChange={setPaymentFilter}>
                        <SelectTrigger className="w-[130px]">
                            <SelectValue placeholder="All Status" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Status</SelectItem>
                            <SelectItem value="unpaid">Unpaid</SelectItem>
                            <SelectItem value="partial">Partial</SelectItem>
                            <SelectItem value="paid">Paid</SelectItem>
                        </SelectContent>
                    </Select>
                    {/* Date Filter */}
                    <div className="flex items-center gap-2">
                        <Input
                            type="date"
                            value={startDate}
                            onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
                            className="w-[130px]"
                        />
                        <span className="text-muted-foreground">to</span>
                        <Input
                            type="date"
                            value={endDate}
                            onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
                            className="w-[130px]"
                        />
                    </div>
                    {/* Quick date presets */}
                    <div className="flex gap-1">
                        <Button variant="outline" size="sm" onClick={() => {
                            const today = format(new Date(), "yyyy-MM-dd");
                            setStartDate(today); setEndDate(today); setPage(1);
                        }}>Today</Button>
                        <Button variant="outline" size="sm" onClick={() => {
                            const today = new Date();
                            const weekAgo = new Date(today); weekAgo.setDate(today.getDate() - 7);
                            setStartDate(format(weekAgo, "yyyy-MM-dd")); setEndDate(format(today, "yyyy-MM-dd")); setPage(1);
                        }}>7 Days</Button>
                        <Button variant="outline" size="sm" onClick={() => {
                            setStartDate(""); setEndDate(""); setPage(1);
                        }}>All</Button>
                    </div>
                </div>

                <div className="flex gap-2">
                    <Button variant="outline" onClick={handleRefresh} disabled={loading}>
                        <RefreshCcw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                        Refresh
                    </Button>
                    <Link to="/dashboard/purchases/add">
                        <Button>
                            <Package className="h-4 w-4 mr-2" />
                            Add Purchase
                        </Button>
                    </Link>
                </div>
            </div>

            {/* Summary Cards */}
            {(() => {
                const purchaseList = Array.isArray(purchases) ? purchases : [];
                const totalOwed = purchaseList.reduce((sum, p) => sum + (parseFloat(p.total) || 0), 0);
                const totalPaid = purchaseList.reduce((sum, p) => sum + (parseFloat(p.amount_paid) || 0), 0);
                const outstanding = totalOwed - totalPaid;
                return (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Card className="p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-muted-foreground">Total Purchases</p>
                                    <p className="text-2xl font-bold">{currency} {totalOwed.toFixed(2)}</p>
                                </div>
                                <div className="h-12 w-12 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                                    <Package className="h-6 w-6 text-blue-600" />
                                </div>
                            </div>
                        </Card>
                        <Card className="p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-muted-foreground">Amount Paid</p>
                                    <p className="text-2xl font-bold text-green-600">{currency} {totalPaid.toFixed(2)}</p>
                                </div>
                                <div className="h-12 w-12 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center">
                                    <DollarSign className="h-6 w-6 text-green-600" />
                                </div>
                            </div>
                        </Card>
                        <Card className="p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-muted-foreground">Outstanding</p>
                                    <p className={`text-2xl font-bold ${outstanding > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                                        {currency} {outstanding.toFixed(2)}
                                    </p>
                                </div>
                                <div className={`h-12 w-12 rounded-full flex items-center justify-center ${outstanding > 0 ? 'bg-orange-100 dark:bg-orange-900' : 'bg-green-100 dark:bg-green-900'}`}>
                                    <DollarSign className={`h-6 w-6 ${outstanding > 0 ? 'text-orange-600' : 'text-green-600'}`} />
                                </div>
                            </div>
                        </Card>
                    </div>
                );
            })()}

            <Card className="enterprise-table-container p-0">
                <div>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Date</TableHead>
                                <TableHead>Invoice #</TableHead>
                                <TableHead>Supplier</TableHead>
                                <TableHead>Total</TableHead>
                                <TableHead>Paid</TableHead>
                                <TableHead>Outstanding</TableHead>
                                <TableHead>Due Date</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading && purchases?.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={9} className="h-24 text-center">
                                        <span className="flex items-center justify-center gap-2"><Loader2 className="animate-spin" /> Loading purchases...</span>
                                    </TableCell>
                                </TableRow>
                            ) : purchases?.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={9} className="h-24 text-center text-muted-foreground">
                                        No purchases found.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                purchases?.map((purchase) => {
                                    const outstanding = Math.max(0, parseFloat(purchase.total) - parseFloat(purchase.amount_paid || 0));
                                    const isOverdue = purchase.due_date && new Date(purchase.due_date) < new Date() && purchase.payment_status !== 'paid';
                                    return (
                                        <TableRow key={purchase.id} className={isOverdue ? 'bg-red-50 dark:bg-red-900/10' : ''}>
                                            <TableCell>{format(new Date(purchase.createdAt), "dd MMM yyyy")}</TableCell>
                                            <TableCell className="font-mono">{purchase.invoice_number || '-'}</TableCell>
                                            <TableCell>{purchase.Supplier?.name || "N/A"}</TableCell>
                                            <TableCell className="font-bold">{currency} {parseFloat(purchase.total).toFixed(2)}</TableCell>
                                            <TableCell className="text-green-600">{currency} {parseFloat(purchase.amount_paid || 0).toFixed(2)}</TableCell>
                                            <TableCell className={outstanding > 0 ? "text-orange-600 font-semibold" : ""}>
                                                {currency} {outstanding.toFixed(2)}
                                            </TableCell>
                                            <TableCell className={isOverdue ? 'text-red-600 font-semibold' : ''}>
                                                {purchase.due_date ? format(new Date(purchase.due_date), "dd MMM yyyy") : '-'}
                                                {isOverdue && ' (OVERDUE)'}
                                            </TableCell>
                                            <TableCell>
                                                <Badge className={statusColor(purchase.payment_status)} variant="outline">
                                                    {purchase.payment_status?.toUpperCase()}
                                                </Badge>
                                            </TableCell>
                                            <TableCell className="text-right relative">
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger asChild>
                                                        <Button variant="ghost" className="h-8 w-8 p-0">
                                                            <DotsThreeVertical className="h-4 w-4" />
                                                        </Button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent align="end">
                                                        <DropdownMenuLabel>Actions</DropdownMenuLabel>
                                                        <DropdownMenuItem onClick={() => navigate(`/dashboard/purchases/${purchase.id}`)}>
                                                            <Eye className="mr-2 h-4 w-4" /> View Details
                                                        </DropdownMenuItem>
                                                        <DropdownMenuItem onClick={() => navigate(`/dashboard/purchases/edit/${purchase.id}`)}>
                                                            <Pencil className="mr-2 h-4 w-4" /> Edit Purchase
                                                        </DropdownMenuItem>
                                                        <DropdownMenuItem onClick={() => navigate(`/dashboard/purchases/returns-all/returns/add?purchase=${purchase.id}`)}>
                                                            <Undo2 className="mr-2 h-4 w-4" /> Return Items
                                                        </DropdownMenuItem>
                                                        <DropdownMenuSeparator />
                                                        {canAcceptPayments && (purchase.payment_status === 'partial' || purchase.payment_status === 'unpaid') && (
                                                            <DropdownMenuItem onClick={() => handleOpenPayment(purchase)}>
                                                                <DollarSign className="mr-2 h-4 w-4" /> Add Payment
                                                            </DropdownMenuItem>
                                                        )}
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </div>

                {/* Pagination Controls */}
                <div className="flex items-center justify-end space-x-2 py-4 px-4">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page === 1 || loading}
                    >
                        Previous
                    </Button>
                    <div className="text-sm text-center min-w-[100px]">
                        Page {page} of {pagination?.totalPages || 1}
                    </div>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(p => p + 1)}
                        disabled={page >= (pagination?.totalPages || 1) || loading}
                    >
                        Next
                    </Button>
                </div>
            </Card>

            {/* Purchase Details Sheet */}
            <Sheet open={detailsSheetOpen} onOpenChange={setDetailsSheetOpen}>
                <SheetContent className="sm:max-w-lg overflow-y-auto">
                    <SheetHeader>
                        <SheetTitle>Purchase Details</SheetTitle>
                        <SheetDescription>
                            {currentPurchase?.invoice_number || 'No Invoice'} - {currentPurchase?.createdAt ? format(new Date(currentPurchase.createdAt), "dd MMM yyyy") : ''}
                        </SheetDescription>
                    </SheetHeader>
                    {currentPurchase && (
                        <div className="space-y-6 mt-6">
                            {/* Supplier Info */}
                            <div className="space-y-2">
                                <h4 className="font-semibold text-sm text-muted-foreground">Supplier</h4>
                                <p className="font-medium">{currentPurchase.Supplier?.name || "N/A"}</p>
                                {currentPurchase.Supplier?.phone && <p className="text-sm text-muted-foreground">{currentPurchase.Supplier.phone}</p>}
                            </div>

                            {/* Items */}
                            {currentPurchase.Stocks && currentPurchase.Stocks.length > 0 && (
                                <div className="space-y-2">
                                    <h4 className="font-semibold text-sm text-muted-foreground">Items Received</h4>
                                    <div className="border">
                                        <Table>
                                            <TableHeader>
                                                <TableRow>
                                                    <TableHead>Product</TableHead>
                                                    <TableHead className="text-right">Qty</TableHead>
                                                    <TableHead className="text-right">Cost</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {currentPurchase.Stocks.map((stock, idx) => (
                                                    <TableRow key={idx}>
                                                        <TableCell>{stock.Product?.title || 'Unknown'}</TableCell>
                                                        <TableCell className="text-right">{stock.quantity}</TableCell>
                                                        <TableCell className="text-right">{currency} {parseFloat(stock.data?.cost || 0).toFixed(2)}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </div>
                                </div>
                            )}

                            {/* Summary */}
                            <div className="space-y-2 border-t pt-4">
                                <div className="flex justify-between font-bold text-lg">
                                    <span>Total</span>
                                    <span>{currency} {parseFloat(currentPurchase.total).toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between text-green-600">
                                    <span>Amount Paid</span>
                                    <span>{currency} {parseFloat(currentPurchase.amount_paid || 0).toFixed(2)}</span>
                                </div>
                                {(currentPurchase.payment_status === 'partial' || currentPurchase.payment_status === 'unpaid') && (
                                    <div className="flex justify-between text-orange-600 font-semibold">
                                        <span>Outstanding</span>
                                        <span>{currency} {Math.max(0, parseFloat(currentPurchase.total) - parseFloat(currentPurchase.amount_paid || 0)).toFixed(2)}</span>
                                    </div>
                                )}
                            </div>

                            {/* Payment Status & Due Date */}
                            <div className="space-y-2 border-t pt-4">
                                <div className="flex justify-between">
                                    <span>Payment Status</span>
                                    <Badge className={statusColor(currentPurchase.payment_status)}>{currentPurchase.payment_status?.toUpperCase()}</Badge>
                                </div>
                                {currentPurchase.due_date && (
                                    <div className="flex justify-between">
                                        <span>Due Date</span>
                                        <span className={new Date(currentPurchase.due_date) < new Date() ? 'text-red-600 font-semibold' : ''}>
                                            {format(new Date(currentPurchase.due_date), "dd MMM yyyy")}
                                            {new Date(currentPurchase.due_date) < new Date() && currentPurchase.payment_status !== 'paid' && ' (OVERDUE)'}
                                        </span>
                                    </div>
                                )}
                            </div>

                            {/* Payment History */}
                            {currentPurchase.payments && currentPurchase.payments.length > 0 && (
                                <div className="space-y-2 border-t pt-4">
                                    <h4 className="font-semibold text-sm text-muted-foreground">Payment History</h4>
                                    <div className="border rounded">
                                        <Table>
                                            <TableHeader>
                                                <TableRow>
                                                    <TableHead>Date</TableHead>
                                                    <TableHead>Method</TableHead>
                                                    <TableHead className="text-right">Amount</TableHead>
                                                    {canManagePayments && <TableHead className="text-right">Actions</TableHead>}
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {currentPurchase.payments.map((payment, idx) => (
                                                    <TableRow key={idx}>
                                                        <TableCell className="text-sm">{format(new Date(payment.payment_date), "dd MMM yyyy")}</TableCell>
                                                        <TableCell className="text-sm">{payment.payment_method}</TableCell>
                                                        <TableCell className="text-right font-medium text-green-600">{currency} {parseFloat(payment.amount).toFixed(2)}</TableCell>
                                                        {canManagePayments && (
                                                            <TableCell className="text-right">
                                                                <div className="flex justify-end gap-1">
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="sm"
                                                                        className="h-7 w-7 p-0"
                                                                        onClick={() => handleEditPayment(payment)}
                                                                    >
                                                                        <Pencil className="h-3 w-3" />
                                                                    </Button>
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="sm"
                                                                        className="h-7 w-7 p-0 text-red-600 hover:text-red-700"
                                                                        onClick={() => setDeletePaymentId(payment.id)}
                                                                    >
                                                                        <Trash2 className="h-3 w-3" />
                                                                    </Button>
                                                                </div>
                                                            </TableCell>
                                                        )}
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </div>
                                </div>
                            )}

                            {/* Actions */}
                            <div className="flex gap-2 pt-4">
                                {canAcceptPayments && (currentPurchase.payment_status === 'partial' || currentPurchase.payment_status === 'unpaid') && (
                                    <Button className="flex-1" onClick={() => { setDetailsSheetOpen(false); handleOpenPayment(currentPurchase); }}>
                                        <DollarSign className="mr-2 h-4 w-4" /> Add Payment
                                    </Button>
                                )}
                            </div>
                        </div>
                    )}
                </SheetContent>
            </Sheet>

            {/* Add/Edit Payment Dialog */}
            <Dialog open={paymentDialogOpen} onOpenChange={(open) => {
                setPaymentDialogOpen(open);
                if (!open) {
                    setEditingPayment(null);
                    setPaymentAmount("");
                    setPaymentMethod("Cash");
                    setPaymentNotes("");
                }
            }}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{editingPayment ? "Edit Payment" : "Add Payment to Supplier"}</DialogTitle>
                        <DialogDescription>
                            {editingPayment
                                ? `Editing payment of ${currency} ${parseFloat(editingPayment.amount).toFixed(2)}`
                                : `Record payment for purchase ${selectedPurchase?.invoice_number || '(No Invoice)'}. Outstanding: ${currency} ${selectedPurchase ? Math.max(0, parseFloat(selectedPurchase.total) - parseFloat(selectedPurchase.amount_paid || 0)).toFixed(2) : '0.00'}`}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label>Amount ({currency})</Label>
                            <Input
                                type="number"
                                step="0.01"
                                min="0"
                                value={paymentAmount}
                                onChange={(e) => setPaymentAmount(e.target.value)}
                                placeholder="Enter amount..."
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Payment Method</Label>
                            <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="Cash">Cash</SelectItem>
                                    <SelectItem value="Momo">Mobile Money</SelectItem>
                                    <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                                    <SelectItem value="Check">Check</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label>Notes (Optional)</Label>
                            <Textarea
                                value={paymentNotes}
                                onChange={(e) => setPaymentNotes(e.target.value)}
                                placeholder="Payment notes..."
                                rows={2}
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setPaymentDialogOpen(false)}>Cancel</Button>
                        <Button
                            onClick={editingPayment ? handleUpdatePayment : handleSubmitPayment}
                            disabled={loading || isPaymentSubmitting || !paymentAmount}
                        >
                            {(loading || isPaymentSubmitting) ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <DollarSign className="mr-2 h-4 w-4" />}
                            {editingPayment ? "Update Payment" : "Add Payment"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Payment Confirmation Dialog */}
            <Dialog open={!!deletePaymentId} onOpenChange={(open) => !open && setDeletePaymentId(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Delete Payment</DialogTitle>
                        <DialogDescription>
                            Are you sure you want to delete this payment? This action cannot be undone and will restore the outstanding balance.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeletePaymentId(null)}>Cancel</Button>
                        <Button
                            variant="destructive"
                            onClick={handleDeletePayment}
                            disabled={isPaymentSubmitting}
                        >
                            {isPaymentSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
                            Delete Payment
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

